import moment from "moment";
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSelector } from "react-redux";

import instance from "../../../api/instance";
import endpoints from "../../../api/endpoints";
import { IAccountStatement } from "../../../mock/_casino-report";
import parseNumber from "../../../utils/parse-number";
import useModal from "../../../hooks/use-modal";
import { RootState } from "../../../redux/store";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

// --------------- constants ---------------

const ACCOUNT_TYPES = [
  { value: "all", label: "All" },
  { value: "balance_report", label: "Deposit/Withdraw Report" },
  { value: "game_report_sports", label: "Sports Report" },
  { value: "game_report_casino", label: "Casino Report" },
  { value: "third_party_casino", label: "Third Party Casino Report" },
];

const SPORTS_GAME_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "Match", label: "Match" },
  { value: "Match1", label: "Match1" },
  { value: "Fancy", label: "Fancy" },
  { value: "Fancy1", label: "Fancy1" },
  { value: "Meter", label: "Meter" },
  { value: "Khado", label: "Khado" },
  { value: "dream", label: "Diam11" },
  { value: "oddeven", label: "oddeven" },
  { value: "daba", label: "Player Battel" },
  { value: "cricketcasino", label: "cricket casino" },
];

interface IBusinessGame {
  game_id: string;
  game_name: string;
  sort_priority: number;
}

interface IClientOption {
  user_id: string;
  user_name: string;
  display_name: string;
  member_type: string;
}

type AccountStatementRow = IAccountStatement & {
  id: string;
  computed_closing_balance?: number;
};

type AccountStatementRowSportsFields = Omit<AccountStatementRow, "_id"> & {
  result?: { runner_name?: string; result?: string };
  game_name?: string;
  event_name?: string;
  market_name?: string;
  runner_name?: string;
  provider_market_id?: string;
  market_id?: string;
  is_casino?: boolean;
  _id?: { market_id?: string; event_id?: string } | string;
};

function getRowMarketId(row: AccountStatementRowSportsFields): string | undefined {
  if (row.market_id) return row.market_id;
  const groupedId = row._id;
  if (groupedId && typeof groupedId === "object" && groupedId.market_id) {
    return groupedId.market_id;
  }
  return undefined;
}

function canLazyLoadMarketBets(row: AccountStatementRowSportsFields): boolean {
  const marketId = getRowMarketId(row);
  return row.is_casino ? !!marketId : !!(row.provider_market_id || marketId);
}

/** Matches Description column / RemarkValue for local table search. */
function getRowDescriptionForSearch(row: AccountStatementRow) {
  const r = row as AccountStatementRowSportsFields;
  if (r.operation_type === "account-open") return "Opening Pts";
  const winnerName = r.result?.runner_name ?? r.result?.result ?? r.runner_name;
  const isSportsRow = [r.game_name, r.event_name, r.market_name, winnerName].some(Boolean);
  const sportsDescription = [r.game_name, r.event_name, r.market_name, winnerName]
    .filter(Boolean)
    .join("/");
  const defaultDescription = r.remark ?? "";
  return isSportsRow ? sportsDescription || defaultDescription : defaultDescription;
}

const CLIENT_SEARCH_MIN_LENGTH = 2;
const DEFAULT_PAGE_SIZE = 25;

// --------------- helpers ---------------

function getValue(amount: number, type: "credit" | "debit") {
  if (amount > 0 && type === "credit") {
    return parseNumber({ value: amount, type: "float" }).toFixed(2);
  }
  if (amount < 0 && type === "debit") {
    return parseNumber({ value: amount, type: "float" }).toFixed(2);
  }
  return "";
}

const PDF_COLUMNS = ["Date", "Credit", "Debit", "Closing", "Description", "Fromto"];

const escapeCsvField = (field: unknown) => {
  const str = String(field ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const mapExportRow = (row: IAccountStatement) => {
  const credit = row.credit_or_debit_amount > 0 ? row.credit_or_debit_amount : "";
  const debit = row.credit_or_debit_amount < 0 ? row.credit_or_debit_amount : "";
  const closingValue = (row as AccountStatementRow).computed_closing_balance ?? row.final_balance;
  return [
    row.created_at ? moment.unix(Number(row.created_at)).format("DD/MM/YYYY HH:mm:ss") : "",
    credit,
    debit,
    parseNumber({ value: closingValue, type: "float" }).toFixed(2),
    row.operation_type === "account-open" ? "Opening Pts" : row.remark ?? "",
    row.from_to ?? "",
  ];
};

const exportCSV = (list: IAccountStatement[]) => {
  const header = PDF_COLUMNS.map(escapeCsvField).join(",");
  const rows = list.map((r) => mapExportRow(r).map(escapeCsvField).join(","));
  const csvContent = [header, ...rows].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "account-statement.csv";
  link.click();
  URL.revokeObjectURL(url);
};

const exportPDF = (list: IAccountStatement[]) => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ orientation: "landscape", format: [190, 297] });
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(14);
  doc.text("Account Statement", pageWidth / 2, 16, { align: "center" });

  autoTable(doc, {
    startY: 22,
    head: [PDF_COLUMNS],
    body: list.map(mapExportRow),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [25, 103, 210] },
  });

  doc.save("account-statement.pdf");
};

// --------------- component ---------------

const AccountStatementPage = () => {
  const isMarket = useModal();
  const loggedInUserId = useSelector((state: RootState) => state.user.userData?.user_id ?? "");

  const [params, setParams] = useState({
    accountType: "all",
    gameName: "all",
    gameType: "all",
    from: moment().subtract(7, "days").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  });

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  const [accountStatement, setAccountStatement] = useState<{ list: AccountStatementRow[]; rowCount: number }>({
    list: [],
    rowCount: 0,
  });
  const [statementLoading, setStatementLoading] = useState(false);
  const [betsLoading, setBetsLoading] = useState(false);
  const [clientRequiredError, setClientRequiredError] = useState(false);

  useSyncGlobalPageLoader(statementLoading || betsLoading);

  // --- business games (fetched once) ---
  const [businessGames, setBusinessGames] = useState<{ sports: IBusinessGame[]; casino: IBusinessGame[] }>({
    sports: [],
    casino: [],
  });
  const businessGamesRequestRef = useRef(false);
  const businessGamesLoadedRef = useRef(false);

  const fetchBusinessGames = useCallback(async () => {
    if (businessGamesLoadedRef.current || businessGamesRequestRef.current) return;
    businessGamesRequestRef.current = true;
    try {
      const { data } = await instance.get(endpoints.business.games);
      setBusinessGames({
        sports: Array.isArray(data?.data?.sports) ? data.data.sports : [],
        casino: Array.isArray(data?.data?.casino) ? data.data.casino : [],
      });
      businessGamesLoadedRef.current = true;
    } catch {
      /* ignore */
    } finally {
      businessGamesRequestRef.current = false;
    }
  }, []);

  // --- client search ---
  const [clientSearchText, setClientSearchText] = useState("");
  const [clientOptions, setClientOptions] = useState<IClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientOption | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientWrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clientSearchRequestIdRef = useRef(0);

  const fetchClients = useCallback(
    async (query: string) => {
      if (!loggedInUserId) {
        return [] as IClientOption[];
      }
      const requestId = ++clientSearchRequestIdRef.current;
      try {
        const { data } = await instance.get("admin/downline-members", {
          params: {
            adminId: loggedInUserId,
            scope: "recursive",
            memberType: "user",
            search: query,
            limit: 10,
          },
        });
        const rawList: any[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.data?.rows)
            ? data.data.rows
            : Array.isArray(data?.data?.members)
              ? data.data.members
              : [];
        const list: IClientOption[] = rawList
          .map((item) => ({
            user_id: item?.user_id ?? item?.admin_id ?? item?.id ?? "",
            user_name: item?.user_name ?? item?.username ?? "",
            display_name:
              item?.display_name ?? item?.displayName ?? item?.user_name ?? item?.username ?? "",
            member_type: String(item?.member_type ?? item?.type ?? item?.user_type ?? "user").toLowerCase(),
          }))
          .filter((item) => item.user_id && item.member_type === "user");
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return list;
      } catch {
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return [] as IClientOption[];
      }
    },
    [loggedInUserId]
  );

  const handleClientSearchChange = (value: string) => {
    setClientSearchText(value);
    if (selectedClient) {
      setSelectedClient(null);
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = value.toLowerCase().trim();
      if (q.length < CLIENT_SEARCH_MIN_LENGTH) {
        setClientOptions([]);
        setShowClientDropdown(true);
        return;
      }
      const source = await fetchClients(q);
      setClientOptions(source);
      setShowClientDropdown(true);
    }, 200);
  };

  const handleSelectClient = (client: IClientOption) => {
    setSelectedClient(client);
    setClientSearchText(client.display_name || client.user_name);
    setShowClientDropdown(false);
    setClientRequiredError(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientWrapperRef.current && !clientWrapperRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- derived state ---
  const isSports = params.accountType === "game_report_sports";
  const isCasino = params.accountType === "game_report_casino";
  const isThirdParty = params.accountType === "third_party_casino";
  const showGameDropdown = !isThirdParty;

  const gameLabel = isSports ? "Sports List" : isCasino ? "Casino List" : "Game Name";

  const gameOptions = useMemo(() => {
    const allOpt = [{ label: "All", value: "all" }];
    if (params.accountType === "balance_report") {
      return [
        ...allOpt,
        { label: "Upper", value: "upper" },
        { label: "Down", value: "down" },
      ];
    }
    if (isSports) {
      return [
        ...allOpt,
        ...businessGames.sports.map((g) => ({ label: g.game_name, value: g.game_name })),
      ];
    }
    if (isCasino) {
      return [
        ...allOpt,
        ...businessGames.casino.map((g) => ({ label: g.game_name, value: g.game_name })),
      ];
    }
    return allOpt;
  }, [params.accountType, isSports, isCasino, businessGames]);

  const handleAccountTypeChange = (value: string) => {
    setParams((prev) => ({ ...prev, accountType: value, gameName: "all", gameType: "all" }));
    if (value === "game_report_sports" || value === "game_report_casino") {
      fetchBusinessGames();
    }
  };

  // --- load data (server pagination; table search filters current page only) ---
  const fetchAccountStatement = useCallback(
    async (targetPage: number) => {
      if (isThirdParty) return;

      // Require an explicit selected client so we only fetch that user's lean ledger.
      if (!selectedClient?.user_id) {
        setAccountStatement({ list: [], rowCount: 0 });
        return;
      }
      if (selectedClient.member_type && selectedClient.member_type !== "user") {
        setAccountStatement({ list: [], rowCount: 0 });
        return;
      }

      const userId = selectedClient.user_id;

      let reportName = "all";
      if (params.accountType === "balance_report") reportName = "balance_report";
      if (isSports || isCasino) reportName = "game_report";

      const startDate = moment(params.from).startOf("day").unix();
      const endDate = moment(params.to).endOf("day").unix();
      const gameName = params.gameName !== "all" ? params.gameName : undefined;
      const isCasinoParam = isSports ? false : isCasino ? true : undefined;
      const offset = Math.max(0, (targetPage - 1) * pageSize);

      setStatementLoading(true);
      try {
        const url = endpoints.userReport.accountStatementLean(
          userId,
          startDate,
          endDate,
          reportName,
          pageSize,
          offset,
          gameName,
          isCasinoParam
        );
        const { data: reportData } = await instance.get(url);

        const rawList = reportData?.data?.accountStatement ?? reportData?.data?.accountGameStatement ?? [];
        const getRowClosingCandidate = (row: any): number | null => {
          if (params.accountType === "game_report_sports") {
            const val = row.sports_balance ?? row.final_balance ?? row.closing_balance;
            return val === null || val === undefined || val === "" ? null : Number(val);
          }
          if (params.accountType === "game_report_casino") {
            const val = row.casino_balance ?? row.final_balance ?? row.closing_balance;
            return val === null || val === undefined || val === "" ? null : Number(val);
          }
          if (params.accountType === "balance_report") {
            if (params.gameName === "upper") {
              const val = row.up_closing ?? row.final_balance ?? row.closing_balance;
              return val === null || val === undefined || val === "" ? null : Number(val);
            }
            if (params.gameName === "down") {
              const val = row.down_closing ?? row.final_balance ?? row.closing_balance;
              return val === null || val === undefined || val === "" ? null : Number(val);
            }
          }
          const val = row.final_balance ?? row.closing_balance;
          return val === null || val === undefined || val === "" ? null : Number(val);
        };

        let runningClosing = Number(reportData?.data?.openingBalance ?? 0);

        const nData: AccountStatementRow[] = Array.isArray(rawList)
          ? rawList.map((i: any) => {
              const explicitClosing = getRowClosingCandidate(i);
              const amount = Number(i.credit_or_debit_amount ?? 0);

              if (explicitClosing !== null && Number.isFinite(explicitClosing)) {
                runningClosing = explicitClosing;
              } else if (Number.isFinite(amount)) {
                runningClosing += amount;
              }

              return {
                ...i,
                id: uuidv4(),
                computed_closing_balance: runningClosing,
              };
            })
          : [];

        setAccountStatement({ list: nData, rowCount: reportData?.data?.count ?? 0 });
      } catch {
        setAccountStatement({ list: [], rowCount: 0 });
      } finally {
        setStatementLoading(false);
      }
    },
    [
      isThirdParty,
      selectedClient?.user_id,
      selectedClient?.member_type,
      params.accountType,
      params.from,
      params.to,
      params.gameName,
      isSports,
      isCasino,
      pageSize,
    ]
  );

  const handleLoad = async () => {
    if (isThirdParty) return;
    if (!selectedClient?.user_id) {
      setClientRequiredError(true);
      setAccountStatement({ list: [], rowCount: 0 });
      return;
    }
    if (selectedClient.member_type && selectedClient.member_type !== "user") {
      setClientRequiredError(true);
      setAccountStatement({ list: [], rowCount: 0 });
      return;
    }
    setClientRequiredError(false);
    setCurrentPage(1);
    await fetchAccountStatement(1);
  };

  const openMarketBets = async (row: AccountStatementRow) => {
    const sportsRow = row as AccountStatementRowSportsFields;
    if (!canLazyLoadMarketBets(sportsRow) || !selectedClient?.user_id) return;

    const marketId = getRowMarketId(sportsRow);
    setBetsLoading(true);
    try {
      const url = endpoints.userReport.accountStatementBets({
        userId: selectedClient.user_id,
        provider_market_id: sportsRow.provider_market_id,
        market_id: marketId,
        is_casino: !!sportsRow.is_casino,
      });
      const { data } = await instance.get(url);
      const bets = Array.isArray(data?.data?.bets) ? data.data.bets : [];
      isMarket.open({ ...row, bets });
    } catch {
      isMarket.open({ ...row, bets: [] });
    } finally {
      setBetsLoading(false);
    }
  };

  const getClosingBalanceValue = (row: AccountStatementRow) => {
    if (typeof row.computed_closing_balance === "number" && Number.isFinite(row.computed_closing_balance)) {
      return row.computed_closing_balance;
    }
    if (params.accountType === "game_report_sports") {
      return row.sports_balance ?? row.final_balance ?? row.closing_balance ?? 0;
    }
    if (params.accountType === "game_report_casino") {
      return row.casino_balance ?? row.final_balance ?? row.closing_balance ?? 0;
    }
    if (params.accountType === "balance_report") {
      if (params.gameName === "upper") {
        return row.up_closing ?? row.final_balance ?? row.closing_balance ?? 0;
      }
      if (params.gameName === "down") {
        return row.down_closing ?? row.final_balance ?? row.closing_balance ?? 0;
      }
    }
    return row.final_balance ?? row.closing_balance ?? 0;
  };

  /** Inline search: subset of rows already loaded for the current server page (not a new API search). */
  const filteredList = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return accountStatement.list;

    return accountStatement.list.filter((row) => {
      const amount = Number(row.credit_or_debit_amount ?? 0);
      const creditStr = getValue(amount, "credit");
      const debitStr = getValue(amount, "debit");
      const closingStr = parseNumber({ value: getClosingBalanceValue(row), type: "float" }).toFixed(2);
      const description = getRowDescriptionForSearch(row);
      const fromTo = String(row.from_to ?? "");

      const haystack = [creditStr, debitStr, closingStr, description, fromTo].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [accountStatement.list, searchText, params.accountType, params.gameName]);

  // --- paging (server-side; total count from API) ---
  const totalRecords = accountStatement.rowCount;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  // --------------- render ---------------
  return (
    <div className="listing-grid report-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Account Statement</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      {/* Filter Form */}
      <div className="report-form mb-0 mt-3">
        <form
          method="post"
          onSubmit={(e) => {
            e.preventDefault();
            handleLoad();
          }}
        >
          <div className="row">
            {/* Account Type */}
            <div className="col-lg-2">
              <div className="form-group">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>Account Type</label>
                <select
                  className="form-control"
                  value={params.accountType}
                  onChange={(e) => handleAccountTypeChange(e.target.value)}
                >
                  {ACCOUNT_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Game Name / Sports List / Casino List — hidden for Third Party Casino */}
            {showGameDropdown && (
              <div className="col-lg-2">
                <div className="form-group">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label>{gameLabel}</label>
                  <select
                    className="form-control"
                    value={params.gameName}
                    onChange={(e) => setParams((prev) => ({ ...prev, gameName: e.target.value }))}
                  >
                    {gameOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Game Type — only for Sports Report */}
            {isSports && (
              <div className="col-lg-2">
                <div className="form-group">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label>Game Type</label>
                  <select
                    className="form-control"
                    value={params.gameType}
                    onChange={(e) => setParams((prev) => ({ ...prev, gameType: e.target.value }))}
                  >
                    {SPORTS_GAME_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Search By Client Name — required for lean statement */}
            <div className="col-lg-2">
              <div className="form-group user-lock-search" style={{ position: "relative" }} ref={clientWrapperRef}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>
                  Search By Client Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control form-control-sm${clientRequiredError ? " is-invalid" : ""}`}
                  placeholder="Enter client name"
                  autoComplete="off"
                  required
                  value={clientSearchText}
                  onChange={(e) => {
                    handleClientSearchChange(e.target.value);
                    if (clientRequiredError) setClientRequiredError(false);
                  }}
                  onFocus={() => {
                    setClientOptions([]);
                    clientSearchRequestIdRef.current += 1;
                    setShowClientDropdown(true);
                  }}
                />
                {clientRequiredError && (
                  <div className="invalid-feedback d-block">
                    Please select a client user from the list.
                  </div>
                )}
                {showClientDropdown && (
                  <ul
                    className="list-group"
                    style={{
                      position: "absolute",
                      zIndex: 1000,
                      width: "100%",
                      maxHeight: "200px",
                      overflowY: "auto",
                      boxShadow: "0 2px 8px rgba(0,0,0,.15)",
                    }}
                  >
                    {clientOptions.length > 0 ? (
                      clientOptions.map((client) => (
                        <li key={client.user_id} className="list-group-item list-group-item-action" style={{ cursor: "pointer", padding: "6px 12px", fontSize: "13px" }}>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-left w-100"
                            style={{ textDecoration: "none", color: "inherit", fontSize: "inherit" }}
                            onClick={() => handleSelectClient(client)}
                          >
                            {client.display_name || client.user_name}
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="list-group-item" style={{ padding: "6px 12px", fontSize: "13px" }}>
                        {clientSearchText.trim()
                          ? "No elements found. Consider changing the search query."
                          : "List is empty."}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>

            {/* From */}
            <div className="col-lg-2">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>From</label>
              <div className="mb-3">
                <input
                  type="date"
                  className="form-control"
                  value={params.from}
                  onChange={(e) => setParams((prev) => ({ ...prev, from: e.target.value }))}
                />
              </div>
            </div>

            {/* To */}
            <div className="col-lg-2">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>To</label>
              <div className="mb-3">
                <input
                  type="date"
                  className="form-control"
                  value={params.to}
                  onChange={(e) => setParams((prev) => ({ ...prev, to: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-2">
              <button type="submit" className="btn btn-primary">
                Load
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Export + Search Row */}
      <div className="row mt-3">
        <div className="col-6">
          {totalRecords > 0 ? (
            <div>
              <button
                type="button"
                className="btn buttons-pdf btn-danger"
                onClick={() => exportPDF(filteredList)}
              >
                <i className="far fa-file-pdf mr-1" />
                PDF
              </button>{" "}
              <div className="d-inline-block">
                <button
                  type="button"
                  className="btn buttons-excel btn-success"
                  onClick={() => exportCSV(filteredList)}
                >
                  <i className="far fa-file-excel mr-1" />
                  Excel
                </button>
              </div>
            </div>
          ) : (
            <div id="tickets-table_length" className="dataTables_length">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="d-inline-flex align-items-center">
                Show&nbsp;
                <select
                  className="custom-select custom-select-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[25, 50, 75, 100, 125, 150].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                &nbsp;entries
              </label>
            </div>
          )}
        </div>
        <div className="col-6 text-right">
          <div className="dataTables_filter text-md-right">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="d-inline-flex align-items-center">
              Search:
              <input
                type="search"
                placeholder="Search..."
                className="form-control ml-1 form-control"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="table-responsive mb-0 mt-2 report-table account-table">
        <div className="table no-footer table-hover table-responsive-sm">
          <table
            id="accStmtTable"
            role="table"
            aria-busy="false"
            aria-colcount={6}
            className="table b-table table-striped table-bordered"
          >
            <colgroup>
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "350px" }} />
              <col style={{ width: "120px" }} />
            </colgroup>
            <thead>
              <tr role="row">
                <th role="columnheader" scope="col" aria-colindex={1} className="position-relative">
                  <div>Date</div>
                </th>
                <th role="columnheader" scope="col" aria-colindex={2} className="position-relative text-right">
                  <div>Credit</div>
                </th>
                <th role="columnheader" scope="col" aria-colindex={3} className="position-relative text-right">
                  <div>Debit</div>
                </th>
                <th role="columnheader" scope="col" aria-colindex={4} className="position-relative text-right">
                  <div>Closing</div>
                </th>
                <th role="columnheader" scope="col" aria-colindex={5} className="position-relative">
                  <div>Description</div>
                </th>
                <th role="columnheader" scope="col" aria-colindex={6} className="position-relative">
                  <div>Fromto</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No data available
                  </td>
                </tr>
              ) : (
                filteredList.map((row, idx) => {
                  const credit = getValue(row.credit_or_debit_amount, "credit");
                  const debit = getValue(row.credit_or_debit_amount, "debit");
                  const closing = parseNumber({ value: getClosingBalanceValue(row), type: "float" }).toFixed(2);
                  const isPositive = row.credit_or_debit_amount > 0;
                  const isOpening = row.operation_type === "account-open";

                  return (
                    <tr key={row.id ?? idx} role="row" tabIndex={0} className="nocursor">
                      <td aria-colindex={1}>
                        {row.created_at
                          ? moment.unix(Number(row.created_at)).format("DD/MM/YYYY HH:mm:ss")
                          : ""}
                      </td>
                      <td aria-colindex={2}>
                        <div className={`text-right ${isPositive && !isOpening ? "text-success" : ""}`}>
                          <span>{isOpening ? "" : credit}</span>
                        </div>
                      </td>
                      <td aria-colindex={3}>
                        <div className={`text-right ${!isPositive && !isOpening ? "text-danger" : ""}`}>
                          <span>{isOpening ? "" : debit}</span>
                        </div>
                      </td>
                      <td aria-colindex={4}>
                        <div className={`text-right ${closing > 0 ? "text-success" : ""}`}>
                          <span>{closing}</span>
                        </div>
                      </td>
                      <td aria-colindex={5}>
                        <div className="wrape-text">
                          {isOpening ? (
                            "Opening Pts"
                          ) : (
                            <RemarkValue row={row} onClickMarket={() => openMarketBets(row)} />
                          )}
                        </div>
                      </td>
                      <td aria-colindex={6}>{row.from_to ?? ""}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="row pt-3">
        <div className="col-12">
          <div className="dataTables_paginate paging_simple_numbers float-right">
            <ul className="pagination pagination-rounded mb-0">
              <ul
                role="menubar"
                aria-disabled="false"
                aria-label="Pagination"
                className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
              >
                <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                  <button
                    role="menuitem"
                    type="button"
                    aria-label="Go to first page"
                    disabled={isFirstPage}
                    tabIndex={isFirstPage ? -1 : 0}
                    className="page-link"
                    onClick={() => {
                      if (isFirstPage) return;
                      setCurrentPage(1);
                      fetchAccountStatement(1);
                    }}
                  >
                    &laquo;
                  </button>
                </li>
                <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                  <button
                    role="menuitem"
                    type="button"
                    aria-label="Go to previous page"
                    disabled={isFirstPage}
                    tabIndex={isFirstPage ? -1 : 0}
                    className="page-link"
                    onClick={() => {
                      if (isFirstPage) return;
                      const targetPage = Math.max(1, currentPage - 1);
                      setCurrentPage(targetPage);
                      fetchAccountStatement(targetPage);
                    }}
                  >
                    &lsaquo;
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <li
                    key={page}
                    role="presentation"
                    className={`page-item ${currentPage === page ? "active" : ""}`}
                  >
                    <button
                      role="menuitemradio"
                      type="button"
                      aria-label={`Go to page ${page}`}
                      aria-checked={currentPage === page}
                      aria-posinset={page}
                      aria-setsize={totalPages}
                      tabIndex={currentPage === page ? 0 : -1}
                      className="page-link"
                      onClick={() => {
                        if (currentPage === page) return;
                        setCurrentPage(page);
                        fetchAccountStatement(page);
                      }}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                  <button
                    role="menuitem"
                    type="button"
                    aria-label="Go to next page"
                    disabled={isLastPage}
                    tabIndex={isLastPage ? -1 : 0}
                    className="page-link"
                    onClick={() => {
                      if (isLastPage) return;
                      const targetPage = Math.min(totalPages, currentPage + 1);
                      setCurrentPage(targetPage);
                      fetchAccountStatement(targetPage);
                    }}
                  >
                    &rsaquo;
                  </button>
                </li>
                <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                  <button
                    role="menuitem"
                    type="button"
                    aria-label="Go to last page"
                    disabled={isLastPage}
                    tabIndex={isLastPage ? -1 : 0}
                    className="page-link"
                    onClick={() => {
                      if (isLastPage) return;
                      setCurrentPage(totalPages);
                      fetchAccountStatement(totalPages);
                    }}
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </ul>
          </div>
        </div>
      </div>

      {isMarket.isOpen && <MarketListPopup open handleClose={isMarket.close} itemData={isMarket.row} />}
    </div>
  );
};

export default AccountStatementPage;

// --------------- sub-components ---------------

interface IRemarkProps {
  row: any;
  onClickMarket: (row: any) => void;
}

const RemarkValue: FC<IRemarkProps> = ({ row = {}, onClickMarket = () => {} }) => {
  const winnerName = row?.result?.runner_name ?? row?.result?.result ?? row?.runner_name;
  const isSportsRow = [row?.game_name, row?.event_name, row?.market_name, winnerName].some(Boolean);
  const sportsDescription = [row?.game_name, row?.event_name, row?.market_name, winnerName]
    .filter(Boolean)
    .join("/");
  const defaultDescription = row?.remark ?? "";
  const description = isSportsRow ? sportsDescription || defaultDescription : defaultDescription;
  const isClickable = isSportsRow && canLazyLoadMarketBets(row);

  if (isClickable) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => onClickMarket(row)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClickMarket(row);
          }
        }}
        style={{
          cursor: "pointer",
          background: "#444",
          padding: "5px 10px",
          textDecoration: "none",
          color: "#fff",
          borderRadius: "3px",
          marginRight: "3px",
          display: "inline-block",
          fontSize: "14px",
        }}
      >
        {description}
      </span>
    );
  }

  return <>{description}</>;
};

interface UserBookPopupPopUp {
  open: boolean;
  handleClose: () => void;
  itemData: any;
}

type IBetRecord = {
  _id: string;
  bet_id: string;
  user_id: string;
  user_name: string;
  nation?: string;
  runner_name?: string;
  rate?: number | string;
  user_rate?: number | string;
  bhav?: number | string;
  bet_amount: number;
  win?: number;
  win_amount?: number;
  profit: number;
  liability: number;
  bet_type: string;
  bet_status: string;
  user_agent?: string;
  browser_detail?: string;
  placed_at: string;
  matched_at: string;
  ip_address: string;
  outcome: string;
  upLine: string;
  odds?: number;
  type?: "back" | "lay";
};

const MarketListPopup: FC<UserBookPopupPopUp> = ({ open, handleClose, itemData }) => {
  const [betType, setBetType] = useState<string>("all");
  const [selectedBetIds, setSelectedBetIds] = useState<Set<string>>(new Set());
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBetType((event.target as HTMLInputElement).value);
  };

  useEffect(() => {
    setBetType("all");
  }, [itemData]);

  useEffect(() => {
    setSelectedBetIds(new Set());
  }, [itemData, betType]);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (modalContentRef.current && !modalContentRef.current.contains(targetNode)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open, handleClose]);

  const allBets = useMemo<IBetRecord[]>(
    () => (Array.isArray(itemData?.bets) ? itemData.bets : []),
    [itemData]
  );

  const betData = useMemo(() => {
    if (betType === "all") return allBets;
    if (betType === "back" || betType === "lay") {
      return allBets.filter((bet) => String(bet?.type ?? "").toLowerCase() === betType);
    }
    if (betType === "deleted") {
      return allBets.filter((bet) =>
        ["deleted", "voided", "cancelled"].includes(String(bet?.bet_status ?? "").toLowerCase())
      );
    }
    return allBets;
  }, [allBets, betType]);

  const getBetId = (bet: IBetRecord, idx: number) => String(bet?._id ?? bet?.bet_id ?? idx);

  const getBetDisplayAmount = (bet: IBetRecord) => {
    const outcome = String(bet?.outcome ?? "").toLowerCase();
    const profitAmount = Number(bet?.profit ?? bet?.win_amount ?? bet?.win ?? 0);
    const liabilityAmount = Number(bet?.liability ?? 0);
    if (outcome === "won") {
      return profitAmount;
    }
    return -Math.abs(liabilityAmount);
  };

  const toggleBetSelection = (betId: string, checked: boolean) => {
    setSelectedBetIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(betId);
      } else {
        next.delete(betId);
      }
      return next;
    });
  };

  const summaryBets = useMemo(() => {
    if (selectedBetIds.size === 0) return betData;
    return betData.filter((bet, idx) => selectedBetIds.has(getBetId(bet, idx)));
  }, [betData, selectedBetIds]);

  const totalBets = summaryBets.length;

  const totalWin = useMemo(
    () =>
      summaryBets.reduce((sum, bet) => sum + getBetDisplayAmount(bet), 0),
    [summaryBets]
  );

  const isFancy = !['match odds', 'bookmaker', 'mini bookmaker'].includes(
    itemData?.market_name?.toLowerCase()
  );

  const detailTitle = [itemData?.game_name, itemData?.league_name, itemData?.event_name, isFancy ? "Normal" :itemData?.market_name]
    .filter(Boolean)
    .join(" -> ");

  const popupWinner = itemData?.result?.runner_name ?? itemData?.result?.result ?? itemData?.runner_name;
  const gameTimeValue = itemData?.event_start_time ?? itemData?.game_time ?? itemData?.event_time ?? itemData?.created_at;
  const gameTimeAsNumber = Number(gameTimeValue);
  const gameTimeText = gameTimeValue
    ? Number.isFinite(gameTimeAsNumber)
      ? moment
          .unix(gameTimeAsNumber > 9999999999 ? Math.floor(gameTimeAsNumber / 1000) : gameTimeAsNumber)
          .format("DD/MM/YYYY HH:mm:ss")
      : moment(gameTimeValue).isValid()
        ? moment(gameTimeValue).format("DD/MM/YYYY HH:mm:ss")
        : "-"
    : "-";

  const formatBetDate = (value: unknown) => {
    if (!value && value !== 0) return "-";
    const n = Number(value);
    if (Number.isFinite(n)) {
      return moment
        .unix(n > 9999999999 ? Math.floor(n / 1000) : n)
        .format("DD/MM/YYYY HH:mm:ss");
    }
    return moment(value as string).isValid() ? moment(value as string).format("DD/MM/YYYY HH:mm:ss") : "-";
  };

  if (!open) return null;

  return (
    <div style={{ position: "absolute", zIndex: 1040 }}>
      <div className="modal fade show" style={{ display: "block" }} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-xl">
          <div className="modal-content" ref={modalContentRef}>
            <header className="modal-header">
              <h5 className="modal-title">Details</h5>
              <button type="button" aria-label="Close" className="close" onClick={handleClose}>
                ×
              </button>
            </header>
            <div className="modal-body">
              <div className="mt-1">{detailTitle || "-"}</div>
              <div className="mt-1">
                <div className="row">
                  <div className="col-6">Winner: {popupWinner ?? "-"}</div>
                  <div className="col-6 text-right">
                    Game Time:
                    <div className="text-right">{gameTimeText}</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="mt-4 modal-chekbox">
                  <div className="custom-control custom-radio custom-control-inline">
                    <input
                      type="radio"
                      id="bet-all"
                      name="betType"
                      value="all"
                      checked={betType === "all"}
                      className="custom-control-input"
                      onChange={handleChange}
                    />
                    <label htmlFor="bet-all" className="custom-control-label">
                      All
                    </label>
                  </div>
                  <div className="custom-control custom-radio custom-control-inline">
                    <input
                      type="radio"
                      id="bet-back"
                      name="betType"
                      value="back"
                      checked={betType === "back"}
                      className="custom-control-input"
                      onChange={handleChange}
                    />
                    <label htmlFor="bet-back" className="custom-control-label">
                      Back
                    </label>
                  </div>
                  <div className="custom-control custom-radio custom-control-inline">
                    <input
                      type="radio"
                      id="bet-lay"
                      name="betType"
                      value="lay"
                      checked={betType === "lay"}
                      className="custom-control-input"
                      onChange={handleChange}
                    />
                    <label htmlFor="bet-lay" className="custom-control-label">
                      Lay
                    </label>
                  </div>
                  <div className="custom-control custom-radio custom-control-inline">
                    <input
                      type="radio"
                      id="bet-deleted"
                      name="betType"
                      value="deleted"
                      checked={betType === "deleted"}
                      className="custom-control-input"
                      onChange={handleChange}
                    />
                    <label htmlFor="bet-deleted" className="custom-control-label">
                      Deleted
                    </label>
                  </div>
                  <div className="custom-control-inline float-right">
                    <h5>
                      Total Bets: <span className="text-success mr-2">{totalBets}</span> Total Win:{" "}
                      <span className={totalWin >= 0 ? "text-success" : "text-danger"}>
                        {parseNumber({ value: totalWin, type: "float" }).toFixed(2)}
                      </span>
                    </h5>
                  </div>
                </div>
                <div className="table-responsive report-table">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="user-name"><div>Username</div></th>
                        <th className="event-name"><div>Nation</div></th>
                        <th className="text-right bet-user-rate"><div>Rate</div></th>
                        <th className="text-right bet-user-rate"><div>Bhav</div></th>
                        <th className="text-right bet-amount"><div>Amount</div></th>
                        <th className="text-right bet-amount"><div>Win</div></th>
                        <th className="bet-date"><div>Date</div></th>
                        <th><div>IP</div></th>
                        <th><div>B Details</div></th>
                        <th className="text-right"><div>Action</div></th>
                      </tr>
                    </thead>
                    <tbody>
                      {betData.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="text-center">
                            No data available
                          </td>
                        </tr>
                      ) : (
                        betData.map((bet, idx) => {
                          const winValue = getBetDisplayAmount(bet);
                          const rateValue = bet?.odds ?? "-";
                          
                          const nationValue = isFancy
                            ? (itemData?.market_name ?? "-")
                            : (bet.runner_name ?? "-");

                          const bhavValue = isFancy
                            ? (bet?.runner_name ?? "-")
                            : "-";

                          const detailTitleText = bet?.browser_detail ?? bet?.user_agent ?? "-";
                          const betDate = formatBetDate(bet?.placed_at || bet?.matched_at);
                          const betId = getBetId(bet, idx);
                          const checkboxId = `bet-action-${betId}`;
                          const isSelected = selectedBetIds.has(betId);
                          const ipAddress = bet.ip_address && bet.ip_address != "" ? bet.ip_address : "127.0.0.1";
                          return (
                            <tr
                              key={bet._id ?? bet.bet_id ?? idx}
                              className={String(bet?.type ?? "").toLowerCase() === "lay" ? "lay-border" : "back-border"}
                            >
                              <td className="user-name"><span>{bet.user_name}</span></td>
                              <td className="event-name">
                                <div>{nationValue}</div>
                              </td>
                              <td className="text-right bet-user-rate"><div>{rateValue}</div></td>
                              <td className="text-right bet-user-rate"><div>{bhavValue}</div></td>
                              <td className="text-right bet-amount">
                                <div>{parseNumber({ value: bet?.bet_amount ?? 0, type: "float" }).toFixed(2)}</div>
                              </td>
                              <td className="text-right bet-amount">
                                <div className={winValue >= 0 ? "text-success" : "text-danger"}>
                                  {parseNumber({ value: winValue, type: "float" }).toFixed(2)}
                                </div>
                              </td>
                              <td className="bet-date"><div>{betDate}</div></td>
                              <td><a href={`https://ipinfo.io/${ipAddress}`} target="_blank" rel="noopener noreferrer">
                                {ipAddress}
                              </a></td>
                              <td>
                                <button
                                  type="button"
                                  title={detailTitleText}
                                  className="btn btn-link p-0 text-success"
                                  style={{ textDecoration: "none" }}
                                >
                                  Detail
                                </button>
                              </td>
                              <td className="text-right">
                                <div className="custom-control custom-checkbox">
                                  <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    value="0"
                                    id={checkboxId}
                                    checked={isSelected}
                                    onChange={(event) => toggleBetSelection(betId, event.target.checked)}
                                  />
                                  <label className="custom-control-label" htmlFor={checkboxId}>
                                    <span className="sr-only">Select bet action</span>
                                  </label>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop"
        role="button"
        tabIndex={0}
        aria-label="Close details modal"
        onClick={handleClose}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClose();
          }
        }}
      />
    </div>
  );
};
